import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import type {
  Campaign,
  CampaignListItem,
  CampaignStatus,
} from "@/schemas/models/campaign";

export const campaignKeys = {
  all: () => ["campaigns"] as const,
  lists: () => [...campaignKeys.all(), "list"] as const,
  details: () => [...campaignKeys.all(), "detail"] as const,
};

type ListCampaignsParams = {
  limit: number;
  offset: number;
  search?: string;
  sort?: string;
  filter?: { status?: CampaignStatus };
};

type ListCampaignsResponse = {
  data: CampaignListItem[];
  meta: { total: number };
};

type UpdateCampaignParams = {
  id: string;
  data: Partial<Pick<Campaign, "name" | "status" | "default_channel">>;
};

export function useUpdateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: UpdateCampaignParams) =>
      apiFetch<Campaign>(`/api/campaigns/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: (updated, { id }) => {
      queryClient.setQueryData<Campaign>(
        [...campaignKeys.details(), id],
        (prev) => (prev ? { ...prev, ...updated } : updated),
      );
      queryClient.invalidateQueries({ queryKey: campaignKeys.all() });
    },
  });
}

type CampaignLookupItem = {
  id: string;
  name: string;
  integration_name: string | null;
};

export const campaignQueries = {
  lookup: () =>
    queryOptions({
      queryKey: [...campaignKeys.all(), "lookup"] as const,
      queryFn: () => apiFetch<CampaignLookupItem[]>("/api/campaigns/lookup"),
      staleTime: 5 * 60 * 1000,
    }),
  list: (params: ListCampaignsParams) =>
    queryOptions({
      queryKey: [...campaignKeys.lists(), params] as const,
      queryFn: () =>
        apiFetch<ListCampaignsResponse>("/api/campaigns/list", {
          method: "POST",
          body: JSON.stringify(params),
        }),
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: [...campaignKeys.details(), id] as const,
      queryFn: () => apiFetch<Campaign>(`/api/campaigns/${id}`),
      staleTime: 5 * 60 * 1000,
    }),
};
